import React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Icon,
  Menu,
  IconButton,
  SvgIcon,
  MenuItem,
  Typography,
  useTheme,
} from '@material-ui/core';
import Loading from 'components/Loading';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import { useStyles } from './Qualifications.styled';
import { isPlainObject, isBoolean } from 'lodash';
import { keyToTitle } from 'utils';
import moment from 'moment';
import i18n from 'i18n-js';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import RequirementWidgetFactory from './widget';
import useWidgetEvents from './widget/useWidgetEvents';
import useQualificationViewModel from './useQualification.viewModel';

const QualificationRequirement = ({
  loading,
  requirements,
  qualification,
  handleBypass,
  handleUpdateDocument,
  handleViewDocument,
  handleUploadDocument,
  handleInvoiceClick,
  handleManualApprove,
  handleClear,
  documents,
  displayRequirementData,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [anchorDocMenu, setAnchorDocMenu] = useState({});
  const [anchorManualMenu, setAnchorManualMenu] = useState({});
  const [anchorDefaultMenu, setAnchorDefaultMenu] = useState({});
  const [anchorEntryFormMenu, setAnchorEntryFormMenu] = useState({});
  const [anchorDlValidationMenu, setDLValidationMenu] = useState({});
  const { localeFormat } = useSelector((state) => state.organizations);
  const widgetEvents = useWidgetEvents();
  const viewModel = useQualificationViewModel({ displayRequirementData });

  /**
   * Handle open menu list
   */
  const handleClick = (event, type, ref) => {
    handleControlsMenu(event, type, ref);
  };

  /**
   * Handle close menu list
   */
  const handleClose = (type, ref) => {
    handleControlsMenu(null, type, ref);
  };

  const handleControlsMenu = (event, type, ref) => {
    const currentTarget = event ? event.currentTarget : null;
    switch (type) {
      case 'document':
        setAnchorDocMenu((prevState) => ({
          prevState,
          [ref]: currentTarget,
        }));
        break;
      case 'manual':
        setAnchorManualMenu((prevState) => ({
          prevState,
          [ref]: currentTarget,
        }));
        break;
      case 'entryform':
        setAnchorEntryFormMenu((prevState) => ({
          prevState,
          [ref]: currentTarget,
        }));
        break;
      case 'dlvalidation':
        setDLValidationMenu((prevState) => ({
          prevState,
          [ref]: currentTarget,
        }));
        break;
      default:
        setAnchorDefaultMenu((prevState) => ({
          prevState,
          [ref]: currentTarget,
        }));
        break;
    }
  };

  /**
   * Render invoice link
   * @param {object} requirement
   */
  const renderInvoice = (requirement) => {
    if (requirement.key === 'BGC' && requirement.data.invoice_id) {
      return (
        <div className="onboarding-status-bgc-check">
          <div>
            <Box
              className={classes.linkBox}
              onClick={() => handleInvoiceClick(requirement.data.invoice_id)}
            >
              <SvgIcon component={InsertDriveFileIcon} className={classes.invoiceIcon} />
              {i18n.t('qualifications.invoice')}: {requirement.data.invoice_id}
            </Box>
          </div>
        </div>
      );
    }
    return null;
  };

  /**
   * Render data of the requirement
   * @param {*} data
   * @param {*} key
   */
  const renderData = (data, key, schema) => {
    const property = schema?.properties?.find((item) => item.name === key);

    return isPlainObject(data) ? (
      <Box>
        <span className="label"> {keyToTitle(key)} </span>
        <Box>{Object.entries(data)?.map(([key, value]) => renderData(value, key, schema))}</Box>
      </Box>
    ) : (
      <Typography key={key} color="textSecondary" style={{ lineHeight: 1, marginBottom: 4 }}>
        <Box fontSize={12} fontWeight="fontWeightMedium">
          {property ? property.label : `${keyToTitle(key)}: `}
        </Box>
        <Box fontSize={12} component="span" data-cy={key}>
          {' '}
          {isBoolean(data) ? (data ? 'True' : 'False') : data === null ? 'null' : data}
        </Box>
      </Typography>
    );
  };

  const actionsList = (requirement, id, type) => {
    const list = [];

    if (requirement.status !== 'PASSED' && !qualification.completed) {
      list.push({
        id: id,
        onClick: () => {
          const event = { target: { checked: !requirement.is_bypassed } };
          handleClose(type, id);
          handleBypass(event, requirement, qualification);
        },
        label: 'qualifications.bypass',
      });
    }

    if (type == 'document') {
      if (requirement.data.id && documents?.length > 0) {
        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleViewDocument(requirement.data.id);
          },
          label: 'common.view',
        });
      }

      list.push({
        id: id,
        onClick: () => {
          handleClose(type, id);
          handleUploadDocument(requirement, qualification.id);
        },
        label: 'common.upload',
      });

      if (requirement.data.id) {
        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleUpdateDocument(requirement.data.id, qualification.id, {
              status: 'APPROVED',
            });
          },
          label: 'qualifications.approve',
        });

        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleUpdateDocument(requirement.data.id, qualification.id, {
              status: 'DECLINED',
            });
          },
          label: 'qualifications.decline',
        });
      }
    }

    if (type == 'manual') {
      if (requirement.status !== 'PASSED') {
        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleManualApprove(requirement, qualification, true);
          },
          label: 'qualifications.approve',
        });
      }
      if (requirement.status === 'NOTSTARTED') {
        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleManualApprove(requirement, qualification, false);
          },
          label: 'qualifications.reject',
        });
      }
    }

    if (
      type == 'entryform' &&
      Array.isArray(displayRequirementData) &&
      displayRequirementData.some((item) => item.slug === requirement.slug)
    ) {
      list.push({
        id: id,
        onClick: () => {
          handleClose(type, id);
          handleManualApprove(requirement, qualification, true);
        },
        label: 'qualifications.approve',
      });
      list.push({
        id: id,
        onClick: () => {
          handleClose(type, id);
          handleManualApprove(requirement, qualification, false);
        },
        label: 'qualifications.decline',
      });
    }

    if (type == 'dlvalidation') {
      if (requirement.status === 'INPROGRESS') {
        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleClear(requirement, qualification);
          },
          label: 'qualifications.clear',
        });
      }
      if (requirement.status !== 'PASSED' && requirement.status !== 'NOTSTARTED') {
        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleManualApprove(requirement, qualification, true);
          },
          label: 'qualifications.approve',
        });
      }
      if (requirement.status === 'INPROGRESS' || requirement.status === 'PASSED') {
        list.push({
          id: id,
          onClick: () => {
            handleClose(type, id);
            handleManualApprove(requirement, qualification, false);
          },
          label: 'qualifications.reject',
        });
      }
    }

    return list;
  };

  /**
   * Render requirement actions
   * @param {object} requirement
   */
  const renderActions = (requirement) => {
    var id = 'actions-default';
    var type = 'default';
    var anchorMenu = {};

    switch (requirement.key) {
      case 'DOCUMENT':
        id = `qualifications-actions-${requirement.slug}`;
        type = 'document';
        anchorMenu = anchorDocMenu;
        break;
      case 'MANUALAPPROVAL':
        id = `manual-menu-${requirement.slug}`;
        type = 'manual';
        anchorMenu = anchorManualMenu;
        break;
      case 'ENTRYFORM':
        id = `entry-form-${requirement.slug}`;
        type = 'entryform';
        anchorMenu = anchorEntryFormMenu;
        break;
      case 'DLVALIDATION':
        id = `dl-validation-${requirement.slug}`;
        type = 'dlvalidation';
        anchorMenu = anchorDlValidationMenu;
        break;
      default:
        id = `actions-default-${requirement.slug}`;
        type = 'default';
        anchorMenu = anchorDefaultMenu;
        break;
    }
    const actions = actionsList(requirement, id, type);
    if (actions.length > 0) return renderActionsMenu(actions, id, type, anchorMenu);
    else return null;
  };

  const renderActionsMenu = (actions, id, type, anchorMenu) => {
    return (
      <Box>
        <IconButton
          aria-controls={id}
          aria-haspopup="true"
          className={classes.button}
          onClick={(e) => handleClick(e, type, id)}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id={id}
          anchorEl={anchorMenu[id]}
          open={Boolean(anchorMenu[id])}
          onClose={() => handleClose(type, id)}
        >
          {actions.map((menuItem, index) => (
            <MenuItem key={index} onClick={menuItem.onClick}>
              {i18n.t(menuItem.label)}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  };

  /**
   * Render start/end date of the requirement
   * @param {string} date
   */
  const renderDate = (date, type) => {
    return (
      <Typography color="textSecondary" style={{ lineHeight: 1, marginBottom: 4 }}>
        <Box fontSize={12} component="span" fontWeight="fontWeightMedium">
          {type}:
        </Box>
        <Box fontSize={12} component="span">
          {' '}
          {moment(date).format(`${localeFormat} hh:mm a`)}
        </Box>
      </Typography>
    );
  };

  return (
    <Box pt={0.8}>
      {!loading ? (
        requirements?.length > 0 ? (
          <>
            {Object.entries(requirements)?.map(([key, value]) => {
              const last = Object.keys(requirements).pop();
              return (
                <Box
                  display="flex"
                  position="relative"
                  data-cy={`requirement-${value.label}`}
                  key={key}
                >
                  <Box
                    className={clsx(classes.timeline, {
                      [classes.lastItem]: last === key,
                    })}
                  >
                    {value.status === 'PASSED' || value.status === 'BYPASSED' ? (
                      <Icon
                        style={{
                          color:
                            value.status === 'PASSED'
                              ? theme.palette.statuses.completed
                              : theme.palette.statuses.inProgress,
                        }}
                        component={CheckCircleIcon}
                        className={classes.passedIcon}
                      />
                    ) : (
                      <Box
                        className={clsx(
                          classes.circle,
                          { [classes.started]: value.status === 'STARTED' },
                          { [classes.inProgress]: value.status === 'INPROGRESS' },
                          { [classes.failed]: value.status === 'FAILED' }
                        )}
                      />
                    )}
                    {last !== key && <Box className={classes.line} />}
                  </Box>
                  <Box pl={3} mb={2} width="100%" display="flex" flexDirection="column">
                    <Typography color="textPrimary" style={{ lineHeight: 1.4 }}>
                      <Box mb={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                        {qualification.slug === 'pre_enable' && value.slug === 'cleanliness'
                          ? 'Cleanliness Exterior'
                          : value.label}
                      </Box>
                    </Typography>
                    <Box display="flex" flexDirection="column" justifyContent="space-between">
                      <Box>
                        {qualification?.slug === 'pre_confirm' &&
                          Object.keys(value?.data || {}).length > 0 && (
                            <Box pb={2}>{renderData(value.data, '', value?.schema)}</Box>
                          )}
                        {viewModel.isDisplayRequirementDataTop(value.slug, value.data) && (
                          <RequirementWidgetFactory
                            name={viewModel.requirementData(value.slug).widget}
                            data={value.data}
                          />
                        )}
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Box>
                          {moment(value.started_time).isValid() &&
                            renderDate(value.started_time, i18n.t('common.start'))}
                          {moment(value.completed_time).isValid() &&
                            renderDate(value.completed_time, i18n.t('common.end'))}
                          {value?.data?.identity && renderData(value.data, '')}
                          {value?.slug === 'additional_info' &&
                            Object.keys(value?.data || {}).length > 0 &&
                            renderData(value.data, '')}
                          {renderInvoice(value)}
                        </Box>
                        <Box display="flex" alignItems="center">
                          {renderActions(value)}
                          {viewModel.isDisplayRequirementData(value.slug, value.data) && (
                            <RequirementWidgetFactory
                              name={`${viewModel.requirementData(value.slug).widget}_collapseButton`}
                              events={widgetEvents}
                            />
                          )}
                        </Box>
                      </Box>
                      {viewModel.isDisplayRequirementData(value.slug, value.data) && (
                        <RequirementWidgetFactory
                          name={viewModel.requirementData(value.slug).widget}
                          data={viewModel.driverLicenseVerification(value.data)}
                          events={widgetEvents}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </>
        ) : (
          <Typography
            style={{
              fontSize: 14,
              margin: 15,
            }}
          >
            {i18n.t('qualifications.qualification_not_found')}
          </Typography>
        )
      ) : (
        <Loading />
      )}
    </Box>
  );
};

QualificationRequirement.propTypes = {
  documents: PropTypes.array,
  handleBypass: PropTypes.func.isRequired,
  requirements: PropTypes.array.isRequired,
  qualification: PropTypes.object.isRequired,
  handleInvoiceClick: PropTypes.func.isRequired,
  handleViewDocument: PropTypes.func.isRequired,
  handleManualApprove: PropTypes.func.isRequired,
  handleUpdateDocument: PropTypes.func.isRequired,
  handleUploadDocument: PropTypes.func.isRequired,
  handleClear: PropTypes.func.isRequired,
  displayRequirementData: PropTypes.array,
  loading: PropTypes.bool,
};

QualificationRequirement.defaultProps = {
  documents: [],
  requirements: [],
  qualification: {},
  handleBypass: () => {},
  handleInvoiceClick: () => {},
  handleViewDocument: () => {},
  handleManualApprove: () => {},
  handleUpdateDocument: () => {},
  handleUploadDocument: () => {},
  handleClear: () => {},
  displayRequirementData: [],
  loading: false,
};

export default QualificationRequirement;
